import type { Theme } from '@mui/material'
import { Alert, Typography } from '@mui/material'
import { makeStyles } from '@mui/styles'
import React from 'react'
import { useIntl } from 'react-intl'

type AlertTypes = 'success' | 'info' | 'warning' | 'error' | undefined

interface Props {
  value?: string
  small?: boolean
  large?: boolean
  severity?: string
}

const useStyles = makeStyles((theme: Theme) => ({
  chip: {
    border: 0,
    outline: 'none',
    padding: 0,
    display: 'flex',
    alignItems: 'center',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  },
  message: {
    padding: 0,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  },
  icon: {
    marginRight: theme.spacing(1),
    marginLeft: 0,
    padding: 0,
  },
  outlinedWarning: {
    color: theme.palette.warning.dark,
  },
  outlinedError: {
    color: theme.palette.error.dark,
  },
  outlinedSuccess: {
    color: theme.palette.success.dark,
  },
}))

const EvaluationStatus = ({ value, small, large }: Props): JSX.Element => {
  const { formatMessage } = useIntl()

  const getAlertState = (): AlertTypes => {
    switch (value) {
      case 'Medium':
      case 'MediumRisk':
      case 'UnderEvaluation':
        return 'warning'

      case 'Low':
      case 'LowRisk':
      case 'ApprovedWithException':
      case 'ApprovedWithoutException':
      case 'Signed':
      case 'MeetsRequirement':
        return 'success'

      case 'Extreme':
      case 'High':
      case 'HighRisk':
      case 'NotApproved':
      case 'NotSigned':
      case 'DoesNotMeetRequirement':
        return 'error'

      default:
        return 'info'
    }
  }
  const severity = getAlertState()
  const classes = useStyles({ small, large, severity })

  if (!value) {
    return <>{formatMessage({ id: 'general.none' })}</>
  }

  return (
    <Alert
      classes={{
        root: classes.chip,
        icon: classes.icon,
        message: classes.message,
        outlinedWarning: classes.outlinedWarning,
        outlinedError: classes.outlinedError,
        outlinedSuccess: classes.outlinedSuccess,
      }}
      variant="outlined"
      severity={severity}
    >
      <Typography variant="subtitle1">{value}</Typography>
    </Alert>
  )
}

export default EvaluationStatus
